import { usePrivy } from "@privy-io/react-auth";
import {
	CheckCircle,
	Circle,
	Loader2,
	Lock,
	ShieldCheck,
	Sparkles,
} from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
	useShinkaiIdentityQuery,
	useUpdateRewardsOptIn,
} from "../hooks/shinkai-identity";
import {
	useDiscordVerifiedStatus,
	useVerifyDiscord,
} from "../hooks/useDiscordVerification";
import { useEvmWallet } from "../hooks/useEvmWallet";
import { useMyQuests } from "../hooks/useMyQuests";

export type RewardStep = {
	id: string;
	name: string;
	description?: string;
	completed: boolean;
};

type RewardsCardProps = {
	variant?: "hero" | "full";
	className?: string;
};

// Step item for checklist
function StepItem({
	step,
	onAction,
}: {
	step: RewardStep;
	onAction?: (stepId: string) => void;
}) {
	return (
		<div
			className={cn(
				"flex items-center justify-between p-3 rounded-lg border transition-colors",
				step.completed
					? "border-success/30 bg-success/5"
					: "border-divider bg-bg-tertiary/30",
			)}
		>
			<div className="flex items-center gap-3 flex-1 min-w-0">
				<div
					className={cn(
						"w-5 h-5 rounded-full flex items-center justify-center shrink-0",
						step.completed
							? "bg-success/20 text-success"
							: "bg-bg-quaternary text-text-tertiary",
					)}
				>
					{step.completed ? (
						<CheckCircle className="w-3.5 h-3.5" />
					) : (
						<Circle className="w-3.5 h-3.5" />
					)}
				</div>
				<div className="flex-1 min-w-0">
					<p
						className={cn(
							"text-sm font-medium",
							step.completed ? "text-success" : "text-text-default",
						)}
					>
						{step.name}
					</p>
					{step.description && (
						<p className="text-[10px] text-text-tertiary">{step.description}</p>
					)}
				</div>
			</div>
			<div className="flex items-center gap-2 shrink-0">
				{step.completed
					? // <span className="text-[10px] text-success font-medium">Done</span>
						null
					: onAction && (
							<Button
								size="xs"
								variant="outline"
								className="h-7 px-3 text-xs"
								onClick={() => onAction(step.id)}
							>
								{step.id === "wallet"
									? "Connect"
									: step.id === "email"
										? "Link"
										: step.id === "discord"
											? "Connect"
											: step.id === "discord_verified"
												? "Verify"
												: "Register"}
							</Button>
						)}
			</div>
		</div>
	);
}

export default function RewardsCard({
	variant = "hero",
	className,
}: RewardsCardProps) {
	const { login, user, authenticated, ready, linkEmail, linkDiscord } =
		usePrivy();
	// Use EVM wallet address (not the Privy embedded Solana wallet)
	const { isConnected } = useEvmWallet();

	// Fetch shinkai identity data using hooks
	const { data: identityData, isLoading: isLoadingRewardsStatus } =
		useShinkaiIdentityQuery();
	const rewardsOptedIn = Boolean(identityData?.rewards);

	const { mutateAsync: updateRewardsOptIn, isPending: isUpdatingRewards } =
		useUpdateRewardsOptIn();

	// Fetch my quests; total points now come from pointsSummary (quest + grants).
	const { data: myQuestsData } = useMyQuests({ enabled: rewardsOptedIn });

	// Fallback to earnedPoints sum if pointsSummary is unavailable.
	const rewardsPoints = useMemo(() => {
		if (typeof myQuestsData?.pointsSummary?.totalPoints === "number") {
			return myQuestsData.pointsSummary.totalPoints;
		}
		if (!myQuestsData?.quests) return 0;
		return myQuestsData.quests.reduce(
			(sum, summary) => sum + summary.earnedPoints,
			0,
		);
	}, [myQuestsData]);

	const pointsFormatted = rewardsPoints.toLocaleString(undefined, {
		maximumFractionDigits: 0,
	});

	// Discord verification
	const { data: discordVerificationData } = useDiscordVerifiedStatus();
	const { mutateAsync: verifyDiscord, isPending: isVerifyingDiscord } =
		useVerifyDiscord();

	// Check requirements for rewards
	const hasWallet = isConnected;
	const hasEmail = Boolean(user?.email?.address);
	const hasDiscord = Boolean(user?.discord);
	const hasDiscordVerified = Boolean(discordVerificationData?.verified);

	const steps: RewardStep[] = useMemo(() => {
		return [
			{
				id: "wallet",
				name: "Connect Wallet",
				description: "Link your wallet address",
				completed: hasWallet,
			},
			{
				id: "email",
				name: "Link Email",
				description: "Verify your email address",
				completed: hasEmail,
			},
			{
				id: "discord",
				name: "Link Discord",
				description: "Join the community",
				completed: hasDiscord,
			},
			{
				id: "discord_verified",
				name: "Discord Verified",
				description: "Be verified in Shinkai Discord server",
				completed: hasDiscordVerified,
			},
		];
	}, [hasWallet, hasEmail, hasDiscord, hasDiscordVerified]);

	const completedSteps = steps.filter((s) => s.completed).length;
	const totalSteps = steps.length;
	const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
	const allStepsCompleted = completedSteps === totalSteps;

	// Handle step actions
	const handleStepAction = (stepId: string) => {
		switch (stepId) {
			case "wallet":
				login();
				break;
			case "email":
				linkEmail();
				break;
			case "discord":
				linkDiscord();
				break;
			case "discord_verified":
				verifyDiscord().catch((error) => {
					console.error("Discord verification failed:", error);
					alert(
						error instanceof Error
							? error.message
							: "Discord verification failed",
					);
				});
				break;
			default:
				break;
		}
	};

	const isEnrolled = allStepsCompleted && rewardsOptedIn;
	const canOptIn = allStepsCompleted && !rewardsOptedIn;

	const handleUpdateRewardsOptIn = async (nextValue: boolean) => {
		try {
			await updateRewardsOptIn(nextValue);
		} catch (error) {
			console.error("Failed to update rewards status:", error);
			alert(
				error instanceof Error
					? error.message
					: "Failed to update rewards status",
			);
		}
	};

	// Loading State
	if (!ready || isLoadingRewardsStatus) {
		return (
			<div
				className={cn(
					"rounded-2xl border border-divider bg-bg-secondary/20 backdrop-blur-xl",
					className,
				)}
			>
				<div className="flex flex-col gap-4 items-center justify-center py-16 px-6">
					<Loader2 className="h-8 w-8 animate-spin text-brand" />
					<p className="text-xs text-text-tertiary">Loading...</p>
				</div>
			</div>
		);
	}

	// Not Connected State
	if (!isConnected || !authenticated) {
		return (
			<div
				className={cn(
					"rounded-xl border border-divider bg-bg-secondary/20 backdrop-blur-xl",
					className,
				)}
			>
				<div className="p-4">
					<div className="flex items-center justify-between gap-4">
						{/* Status */}
						<div className="flex items-center gap-2 shrink-0">
							<div className="w-8 h-8 rounded-full bg-bg-tertiary flex items-center justify-center border border-divider">
								<Lock className="w-4 h-4 text-text-tertiary" />
							</div>
							<div>
								<div className="text-xs font-medium text-text-default">
									Unlock Rewards
								</div>
								<div className="text-[10px] text-text-tertiary">
									Connect your wallet to earn rewards
								</div>
							</div>
						</div>

						{/* Action */}
						<div className="flex items-center gap-2 shrink-0">
							<Button onClick={login} variant="default" size="xs">
								Connect Wallet
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Enrolled State - Show Points
	if (isEnrolled) {
		return (
			<div
				className={cn(
					"rounded-xl border border-divider bg-bg-secondary/20 backdrop-blur-xl",
					className,
				)}
			>
				<div className="p-4">
					<div className="flex items-center justify-between gap-4">
						{/* Enrollment Status & Points */}
						<div className="flex items-center gap-4 flex-1 min-w-0">
							<div className="flex items-center gap-2 shrink-0">
								<div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center border border-success/20">
									<ShieldCheck className="w-4 h-4 text-success" />
								</div>
								<div>
									<div className="text-xs font-medium text-text-default">
										Enrolled
									</div>
									<div className="text-[10px] text-text-tertiary">
										Active Rewards
									</div>
								</div>
							</div>
							<div className="h-8 w-px bg-divider shrink-0" />
							<div className="min-w-0">
								<div className="text-xs text-text-tertiary mb-0.5">Points</div>
								<div className="text-xl font-semibold text-text-default tracking-tight truncate">
									{pointsFormatted}
								</div>
							</div>
						</div>

						{/* Action */}
						<div className="flex items-center gap-2 shrink-0">
							<Link to="/quests">
								<Button
									variant="outline"
									size="xs"
									className="border-divider hover:bg-bg-tertiary"
								>
									Explore Quests
								</Button>
							</Link>
							{variant === "full" && (
								<Button
									variant="link"
									size="xs"
									onClick={() => handleUpdateRewardsOptIn(false)}
									disabled={isUpdatingRewards}
									className="text-text-tertiary hover:text-text-default h-auto p-0"
								>
									{isUpdatingRewards ? "..." : "Opt out"}
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Setup State - Requirements & Identity Selection
	return (
		<>
			<div
				className={cn(
					"rounded-2xl border border-divider bg-bg-secondary/20 backdrop-blur-xl",
					className,
				)}
			>
				<div className="p-6 space-y-5">
					{/* Header with Progress */}
					<div className="flex justify-between items-start">
						<div>
							<p className="text-xs font-medium text-text-tertiary mb-1">
								Rewards Setup
							</p>
							<h3 className="text-xl font-semibold text-text-default tracking-tight">
								Opt-in to Earn
							</h3>
						</div>
						<div className="text-right">
							<div className="text-[10px] text-text-tertiary uppercase tracking-wider font-medium">
								Progress
							</div>
							<div className="text-lg font-semibold text-text-default">
								{completedSteps}/{totalSteps}
							</div>
						</div>
					</div>

					{/* Progress Bar */}
					<div className="space-y-2">
						<div className="h-1.5 w-full bg-bg-secondary rounded-full overflow-hidden">
							<div
								className="h-full bg-linear-to-r from-brand to-purple-500 rounded-full transition-all duration-500"
								style={{ width: `${progress}%` }}
							/>
						</div>
						<p className="text-[10px] text-text-tertiary">
							{allStepsCompleted
								? "All requirements completed! Opt in to start earning points."
								: "Complete these steps to start earning points."}
						</p>
					</div>

					{/* Steps Checklist */}
					<div className="space-y-2">
						{steps.map((step) => (
							<StepItem key={step.id} step={step} onAction={handleStepAction} />
						))}
					</div>

					{/* CTA for first incomplete step (hero variant only) */}
					{/* {variant === "hero" && !hasIdentity && (
            <Link to="/register" className="block">
              <Button className="w-full" variant="default">
                Register Identity
              </Button>
            </Link>
          )} */}

					{/* Opt-in Button */}
					{canOptIn && (
						<>
							<div className="h-px bg-divider" />
							<Button
								onClick={() => handleUpdateRewardsOptIn(true)}
								disabled={isUpdatingRewards}
								className="w-full gap-2"
							>
								{isUpdatingRewards ? (
									<Loader2 className="w-4 h-4 animate-spin" />
								) : (
									<Sparkles className="w-4 h-4" />
								)}
								{isUpdatingRewards ? "Enrolling..." : "Opt in to Rewards"}
							</Button>
						</>
					)}
				</div>
			</div>
		</>
	);
}
